// Font loader utility for Calibri fonts in PDF generation
import jsPDF from 'jspdf';

// Font data URLs for Calibri fonts
const CALIBRI_FONTS = {
  regular: '/fonts/calibri-regular.ttf',
  bold: '/fonts/calibri-bold.ttf',
  italic: '/fonts/calibri-italic.ttf',
  boldItalic: '/fonts/calibri-bold-italic.ttf'
};

// Cache for loaded fonts
const fontCache = new Map<string, string>();

// Load font as base64 data URL
const loadFontAsBase64 = async (fontPath: string): Promise<string> => {
  if (fontCache.has(fontPath)) {
    return fontCache.get(fontPath)!;
  }

  try {
    const response = await fetch(fontPath);
    if (!response.ok) {
      throw new Error(`Failed to load font: ${response.status}`);
    }
    
    const arrayBuffer = await response.arrayBuffer();
    const uint8Array = new Uint8Array(arrayBuffer);
    
    const base64 = btoa(String.fromCharCode.apply(null, Array.from(uint8Array)));
    const dataUrl = `data:font/truetype;base64,${base64}`;
    
    fontCache.set(fontPath, dataUrl);
    return dataUrl;
  } catch (error) {
    console.error('Error loading font:', fontPath, error);
    throw error;
  }
};

// Register Calibri fonts with jsPDF
export const registerCalibriFonts = async (doc: jsPDF): Promise<void> => {
  try {
    // Load all Calibri font variants
    const [regular, bold, italic, boldItalic] = await Promise.all([
      loadFontAsBase64(CALIBRI_FONTS.regular),
      loadFontAsBase64(CALIBRI_FONTS.bold),
      loadFontAsBase64(CALIBRI_FONTS.italic),
      loadFontAsBase64(CALIBRI_FONTS.boldItalic)
    ]);

    // Add fonts to jsPDF
    doc.addFileToVFS('calibri-regular.ttf', regular);
    doc.addFileToVFS('calibri-bold.ttf', bold);
    doc.addFileToVFS('calibri-italic.ttf', italic);
    doc.addFileToVFS('calibri-bold-italic.ttf', boldItalic);

    // Add fonts to the document
    doc.addFont('calibri-regular.ttf', 'calibri', 'normal');
    doc.addFont('calibri-bold.ttf', 'calibri', 'bold');
    doc.addFont('calibri-italic.ttf', 'calibri', 'italic');
    doc.addFont('calibri-bold-italic.ttf', 'calibri', 'bolditalic');

  } catch (error) {
    console.error('Error registering Calibri fonts:', error);
    // Fallback to helvetica if Calibri fails to load
    console.warn('Falling back to helvetica font');
  }
};

// Helper function to set Calibri font with fallback
export const setCalibriFont = (doc: jsPDF, style: 'normal' | 'bold' | 'italic' | 'bolditalic' = 'normal'): void => {
  try {
    doc.setFont('calibri', style);
  } catch (error) {
    // Fallback to helvetica if Calibri is not available
    console.warn('Calibri font not available, using helvetica fallback');
    doc.setFont('helvetica', style);
  }
};
